import getTableViewFromTableQuery from './getTableViewFromTableQuery';
import onInitialTableLoadComplete from './onInitialTableLoadComplete';
import { PerformanceDatapoint, returnTopExecutingActionDatapoint } from 'owa-analytics';
import { resetSelection } from 'owa-mail-actions';
import { lazyCleanSearchTableState, lazyLoadSearchTable } from 'owa-mail-execute-search-actions';
import loadInitialGroupTable from 'owa-mail-list-actions/lib/actions/table/loadInitialGroupTable';
import loadInitialMailTable from 'owa-mail-list-actions/lib/actions/table/loadInitialMailTable';
import onLoadInitialRowsSucceeded from 'owa-mail-list-actions/lib/actions/table/onLoadInitialRowsSucceeded';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { ActionSource } from 'owa-mail-store';
import { MailListItemSelectionSource } from 'owa-mail-store';
import getPersonaPhotoQueue from 'owa-persona/lib/actions/getPersonaPhotoQueue';
import { action, select } from 'satcheljs/lib/legacy';
import {
    listViewStore,
    ListViewStore,
    TableQuery,
    TableQueryType,
    TableView,
} from 'owa-mail-list-store';
import tombstoneOperations from 'owa-mail-list-tombstone';

export interface LoadTableViewFromTableQueryState {
    listViewStore: ListViewStore;
}

/**
 * Called before loading a new table
 * @param oldTableView the old table view
 */
function onBeforeTableLoad(oldTableView: TableView) {
    // always clear the getPersonaPhotoQueue when switching the table
    getPersonaPhotoQueue.clear();

    // Always reset the selection on the old table.
    resetSelection(oldTableView, MailListItemSelectionSource.Reset);

    // Clear entries from the tombstone for the old table
    if (oldTableView.tableQuery.type === TableQueryType.Folder) {
        tombstoneOperations.clearMapForFolder(oldTableView.serverFolderId);
    }
}

/**
 * Called after loading the new table
 * @param oldTableView the old table view
 */
function onAfterTableLoad(oldTableView: TableView, newTableView: TableView) {
    // Clean the old table View's state.
    // Currently we do not have to clean up anything on a non-search (or Folder) table
    // as we are caching them and the only clean up that we perform is the resetting the selection
    if (
        oldTableView.tableQuery.type === TableQueryType.Search &&
        oldTableView.id !== newTableView.id
    ) {
        const actionDatapoint = returnTopExecutingActionDatapoint();
        lazyCleanSearchTableState(oldTableView, actionDatapoint);
    }
}

const loadTableViewFromTableQueryActionName = 'loadTableViewFromTableQuery';
export { loadTableViewFromTableQueryActionName };

/**
 * Load the table from the table query
 * @param tableQuery the table query
 * @param loadTableViewDatapoint - performance datapoint that starts when the user initiates the table load
 * @param actionSource action that initiated the switch folder action
 * @param state the state which contains the list view store
 * @return a promise that resolves when the load table from server has completed
 */
export default action(loadTableViewFromTableQueryActionName)(
    select({
        listViewStore: () => listViewStore,
    })(function loadTableViewFromTableQuery(
        tableQuery: TableQuery,
        loadTableViewDatapoint?: PerformanceDatapoint,
        actionSource?: ActionSource,
        state?: LoadTableViewFromTableQueryState
    ): Promise<void> {
        const oldTableView = getSelectedTableView();
        const newTableView = getTableViewFromTableQuery(tableQuery);

        // Use the reference from the store (http://aka.ms/mobx4)
        tableQuery = newTableView.tableQuery;

        // 1. Set the current selected table in list view store to the new table
        state.listViewStore.selectedTableViewId = newTableView.id;

        // 2. Call onBeforeTableLoad before loading new table
        if (oldTableView) {
            // oldTableView is null when first loading OWA as there is no previously selected table
            onBeforeTableLoad(oldTableView);
        }

        // 3. Load New Table
        let loadTablePromise: Promise<void>;
        switch (tableQuery.type) {
            case TableQueryType.Search:
                loadTablePromise = lazyLoadSearchTable.importAndExecute(
                    newTableView,
                    onInitialTableLoadComplete,
                    onLoadInitialRowsSucceeded,
                    actionSource,
                    loadTableViewDatapoint
                );
                break;

            case TableQueryType.Group:
                loadTablePromise = loadInitialGroupTable(
                    newTableView,
                    onInitialTableLoadComplete,
                    actionSource
                );
                break;
            case TableQueryType.Folder:
                loadTablePromise = loadInitialMailTable(
                    newTableView,
                    onInitialTableLoadComplete,
                    actionSource
                );
                break;

            default:
                throw new Error(
                    'Only Folder, Group or Search Table types are supported in Table Loading'
                );
        }

        // 4. Call onAfterTableLoad after loading new table
        if (oldTableView) {
            onAfterTableLoad(oldTableView, newTableView);
        }

        return loadTablePromise;
    })
);
